import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { log } from 'utils';
import _ from 'underscore';

import AddMediaIcon from '../../assets/icon/add-media.png';
import SideBarPublish from 'components/SideBarPublish';
import { addWebContents, updateWebContents } from 'state/actions/webContents';
import { imgUrl } from 'utils/url';
import './AddPostingPage.css';

const AddPostingForm = ({ closeAddPosting, predefinedData }) => {
  const { siteId } = useParams();

  const { loading, userData } = useSelector(
    (state) => ({
      loading: state.webContents.loading,
      userData: state.auth.userData,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  const [lastEdited, setLastEdited] = useState(new Date());

  useEffect(() => {
    if (!predefinedData || _.isEmpty(predefinedData)) return;

    setTitle(predefinedData.title || '');
    setPermalink(predefinedData.permalink || '');
    setHtml(predefinedData.content || '');

    if (predefinedData.category) {
      setSelectedCategory(predefinedData.category);
    }

    if (predefinedData.status) {
      setPostStatus(
        predefinedData.status.charAt(0).toUpperCase() +
          predefinedData.status.slice(1)
      );
    }

    if (predefinedData.visibility) {
      setVisibility(
        predefinedData.visibility.charAt(0).toUpperCase() +
          predefinedData.visibility.slice(1)
      );
    }

    if (predefinedData.publishedOn) {
      setPublishDate(new Date(predefinedData.publishedOn));
    }

    if (predefinedData.tags && predefinedData.tags.length > 0) {
      setTagList(predefinedData.tags);
    }

    if (predefinedData.logo) {
      setMediaFiles([
        {
          id: Date.now(),
          file: null,
          name: predefinedData.logo,
          preview: `${imgUrl}/media/${predefinedData.logo}`,
          isOld: true,
        },
      ]);
    }

    if (predefinedData.customFields && predefinedData.customFields.length > 0) {
      const mappedFields = predefinedData.customFields.map((field) => ({
        id: field._id || Date.now() + Math.random(),
        name: field.name,
        value: field.value,
      }));
      setCustomFields(mappedFields);
    }

    if (predefinedData.updatedAt) {
      setLastEdited(new Date(predefinedData.updatedAt));
    }
  }, [predefinedData]);

  const [title, setTitle] = useState('');
  const [permalink, setPermalink] = useState('');
  const [isEditingPermalink, setIsEditingPermalink] = useState(false);
  const [isSlugEdited, setIsSlugEdited] = useState(false);

  const [customFields, setCustomFields] = useState([
    { id: 1, name: '', value: '' },
  ]);

  const addCustomField = () => {
    setCustomFields([...customFields, { id: Date.now(), name: '', value: '' }]);
  };

  const updateCustomField = (id, field, value) => {
    setCustomFields(
      customFields.map((cf) => (cf.id === id ? { ...cf, [field]: value } : cf))
    );
  };

  const deleteCustomField = (id) => {
    setCustomFields(customFields.filter((cf) => cf.id !== id));
  };

  const [html, setHtml] = useState('');

  function onChangeEditor(e) {
    setHtml(e.target.value);
    setLastEdited(new Date());
  }

  const getWordCount = (htmlContent) => {
    const text = htmlContent.replace(/<[^>]*>/g, ' ');
    const words = text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    return words.length;
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);

    if (!isSlugEdited) {
      setPermalink(generateSlug(value));
    }
  };

  const wordCount = getWordCount(html);

  const formatLastEdited = (date) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;

    return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
  };

  const [selectedCategoryPost, setSelectedCategory] = useState('Category 1');

  const [mediaFiles, setMediaFiles] = useState([]);

  const categories = [
    'Category 1',
    'Category 2',
    'Category 3',
    'Category 4',
    'Category 5',
  ];

  const [postStatus, setPostStatus] = useState('Published');
  const [visibility, setVisibility] = useState('Public');
  const [publishDate, setPublishDate] = useState(new Date());
  const [tagInput, setTagInput] = useState('');
  const [tagList, setTagList] = useState([]);

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);

    const mappedFiles = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
    }));

    setMediaFiles((prev) => [...prev, ...mappedFiles]);
  };

  const handleSavePost = async () => {
    const formData = new FormData();

    formData.append('title', title);
    formData.append('siteId', siteId);
    formData.append('permalink', permalink);
    formData.append('content', html);
    formData.append('status', postStatus.toLowerCase());
    formData.append('visibility', visibility.toLowerCase());
    formData.append('category', selectedCategoryPost);
    if (mediaFiles.length > 0 && mediaFiles[0].file) {
      formData.append('file', mediaFiles[0].file);
    }
    formData.append('userId', userData.id);
    try {
      if (predefinedData && predefinedData._id) {
        await dispatch(
          updateWebContents(predefinedData._id, formData, () =>
            closeAddPosting()
          )
        );
      } else {
        await dispatch(addWebContents(formData, () => closeAddPosting()));
      }
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const addTag = () => {
    if (!tagInput.trim()) return;

    const newTags = tagInput
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter((tag) => tag.length > 0);

    const uniqueTags = [
      ...tagList,
      ...newTags.filter((tag) => !tagList.includes(tag)),
    ];

    setTagList(uniqueTags);
    setTagInput('');
  };

  const removeTag = (tagToRemove) => {
    setTagList(tagList.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div className="add-posting-page-library">
      <div
        style={{
          width: '75%',
          height: '100%',
          paddingLeft: '12px',
        }}
      >
        <span className="text-title">Add Post</span>
        <div
          style={{
            margin: '0 auto',
            padding: '8px 20px 8px 0px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#1e1e1e',
                marginBottom: '8px',
              }}
            >
              Title
            </label>
            <input
              type="text"
              placeholder="Enter title"
              value={title}
              onChange={handleTitleChange}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f9f9f9',
                outline: 'none',
              }}
            />
          </div>

          {/* Permalink Section */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px',
              fontSize: '14px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ color: '#666' }}>Permalink:</span>

            {!isEditingPermalink ? (
              <>
                <span style={{ color: '#2271b1' }}>
                  http://localhost:8080/{permalink}
                </span>

                <button
                  onClick={() => setIsEditingPermalink(true)}
                  style={{
                    padding: '4px 12px',
                    fontSize: '13px',
                    color: '#2271b1',
                    backgroundColor: 'white',
                    border: '1px solid #2271b1',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Change Permalink Structure
                </button>
              </>
            ) : (
              <>
                <span style={{ color: '#666' }}>http://localhost:8080/</span>

                <input
                  type="text"
                  value={permalink}
                  onChange={(e) => {
                    setPermalink(generateSlug(e.target.value));
                    setIsSlugEdited(true);
                  }}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '200px',
                  }}
                />

                <button
                  onClick={() => setIsEditingPermalink(false)}
                  style={{
                    padding: '4px 12px',
                    fontSize: '13px',
                    backgroundColor: '#2271b1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>

                <button
                  onClick={() => setIsEditingPermalink(false)}
                  style={{
                    padding: '4px 12px',
                    fontSize: '13px',
                    backgroundColor: '#f3f4f6',
                    color: '#333',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>

          {/* Add Media Button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '16px',
            }}
          >
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              id="media-upload"
              style={{ display: 'none' }}
              onChange={handleMediaChange}
            />

            <button
              onClick={() => document.getElementById('media-upload').click()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                fontSize: '12px',
                color: '#64D5D2',
                backgroundColor: 'white',
                border: '1px solid #64D5D2',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              <img
                src={AddMediaIcon}
                style={{
                  width: '20px',
                  height: '20px',
                }}
                alt="add media"
              />
              Add Media
            </button>
          </div>

          {mediaFiles.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {mediaFiles.map((media) => (
                  <div
                    key={media.id}
                    style={{
                      position: 'relative',
                      width: '100px',
                      height: '100px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={media.preview}
                      alt={media.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <button
                      onClick={() =>
                        setMediaFiles((prev) =>
                          prev.filter((m) => m.id !== media.id)
                        )
                      }
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        background: 'red',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Editor Section - using textarea since NMS doesn't have react-simple-wysiwyg */}
          <div
            style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              marginBottom: '24px',
            }}
          >
            <textarea
              value={html}
              onChange={onChangeEditor}
              style={{
                width: '100%',
                minHeight: '300px',
                border: 'none',
                padding: '16px',
                fontSize: '14px',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                resize: 'vertical',
                outline: 'none',
              }}
              placeholder="Write your content here..."
            />

            {/* Footer */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderTop: '1px solid #ddd',
                fontSize: '13px',
                color: '#666',
              }}
            >
              <span>Word count: {wordCount}</span>
              <span>Last edited on {formatLastEdited(lastEdited)}</span>
            </div>
          </div>

          {/* Add Custom Field Button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '16px',
            }}
          >
            <button
              onClick={addCustomField}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                fontSize: '12px',
                color: '#64D5D2',
                backgroundColor: 'white',
                border: '1px solid #64D5D2',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Add Custom Field
            </button>
          </div>

          {/* Custom Fields Section */}
          <div>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #e5e7eb',
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#6b7280',
                      textAlign: 'left',
                      padding: '16px 20px',
                      borderBottom: '1px solid #e5e7eb',
                      borderRight: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#6b7280',
                      textAlign: 'left',
                      padding: '16px 20px',
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb',
                    }}
                  >
                    Value
                  </th>
                </tr>
              </thead>

              <tbody>
                {customFields.map((field, index) => (
                  <React.Fragment key={field.id}>
                    <tr>
                      <td
                        style={{
                          padding: '20px',
                          borderBottom: '1px solid #e5e7eb',
                          borderRight: '1px solid #e5e7eb',
                          verticalAlign: 'top',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Enter title"
                          value={field.name}
                          onChange={(e) =>
                            updateCustomField(field.id, 'name', e.target.value)
                          }
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            fontSize: '15px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            backgroundColor: '#f9fafb',
                            outline: 'none',
                            transition: 'all 0.2s',
                            color: '#9ca3af',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#3b82f6';
                            e.target.style.backgroundColor = 'white';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.backgroundColor = '#f9fafb';
                          }}
                        />
                      </td>
                      <td
                        style={{
                          padding: '20px',
                          borderBottom: '1px solid #e5e7eb',
                          verticalAlign: 'top',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Text Area"
                          value={field.value}
                          onChange={(e) =>
                            updateCustomField(field.id, 'value', e.target.value)
                          }
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            fontSize: '15px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            backgroundColor: '#f9fafb',
                            outline: 'none',
                            transition: 'all 0.2s',
                            color: '#9ca3af',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#3b82f6';
                            e.target.style.backgroundColor = 'white';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.backgroundColor = '#f9fafb';
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan="2"
                        style={{
                          padding: '16px 20px',
                          borderBottom:
                            index === customFields.length - 1
                              ? 'none'
                              : '1px solid #e5e7eb',
                          textAlign: 'right',
                        }}
                      >
                        <button
                          onClick={() => deleteCustomField(field.id)}
                          style={{
                            padding: '8px 16px',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#F62C2C',
                            backgroundColor: 'white',
                            border: '1px solid #F62C2C',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            marginRight: '12px',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#fef2f2';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'white';
                          }}
                        >
                          Delete
                        </button>
                        <button
                          style={{
                            padding: '8px 16px',
                            fontSize: '12px',
                            fontWeight: '500',
                            color: '#64D5D2',
                            backgroundColor: 'white',
                            border: '1px solid #64D5D2',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}

                <tr>
                  <td colSpan="2">
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#000000',
                        border: 'none',
                        borderTop: '1px solid #EDEDED',
                        width: '100%',
                        padding: '4px 10px',
                      }}
                    >
                      Custom fields can be used to add extra metadata to a post
                      that you can
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Footer */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '30px 0px',
              }}
            >
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 10px',
                  fontSize: '12px',
                  color: '#64D5D2',
                  backgroundColor: 'white',
                  border: '1px solid #64D5D2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Preview
              </button>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    cursor: 'pointer',
                    color: '#F62C2C',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                  onClick={closeAddPosting}
                >
                  Cancel
                </span>
                <button
                  onClick={handleSavePost}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    fontSize: '12px',
                    color: '#64D5D2',
                    backgroundColor: 'white',
                    border: '1px solid #64D5D2',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          width: '25%',
          height: '100%',
          border: 'none',
          borderLeft: '1px solid #EDEDED',
        }}
      >
        <div
          style={{
            width: '400px',
            backgroundColor: 'white',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            padding: '0',
          }}
        >
          <SideBarPublish
            postStatus={postStatus}
            setPostStatus={setPostStatus}
            visibility={visibility}
            setVisibility={setVisibility}
            publishDate={publishDate}
            setPublishDate={setPublishDate}
          />

          {/* CATEGORIES Section */}
          <div style={{ borderBottom: '1px solid #e5e7eb', padding: '24px' }}>
            <h3
              style={{
                margin: '0 0 20px 0',
                fontSize: '13px',
                fontWeight: '600',
                color: '#9ca3af',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              CATEGORIES
            </h3>

            <div style={{ marginBottom: '16px' }}>
              {categories.map((category) => (
                <label
                  key={category}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 0',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: '2px solid #e5e7eb',
                      backgroundColor:
                        selectedCategoryPost === category ? '#06b6d4' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    {selectedCategoryPost === category && (
                      <svg
                        style={{
                          width: '12px',
                          height: '12px',
                          color: 'white',
                        }}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <input
                    type="radio"
                    name="category"
                    value={category}
                    checked={selectedCategoryPost === category}
                    onChange={() => setSelectedCategory(category)}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: '15px', color: '#1f2937' }}>
                    {category}
                  </span>
                </label>
              ))}
            </div>

            <button
              style={{
                background: 'none',
                border: 'none',
                color: '#06b6d4',
                cursor: 'pointer',
                fontSize: '15px',
                padding: '0',
                fontWeight: '500',
              }}
            >
              + Add Category
            </button>
          </div>

          {/* TAGS Section */}
          <div style={{ padding: '24px' }}>
            <h3
              style={{
                margin: '0 0 20px 0',
                fontSize: '13px',
                fontWeight: '600',
                color: '#9ca3af',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              TAGS
            </h3>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Add tags (press Enter or comma)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  outline: 'none',
                  color: '#374151',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#06b6d4')}
                onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
              />

              <button
                onClick={addTag}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#06b6d4',
                  backgroundColor: 'white',
                  border: '2px solid #06b6d4',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
            </div>
            {tagList.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                {tagList.map((tag) => (
                  <div
                    key={tag}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: '#ecfeff',
                      color: '#06b6d4',
                      padding: '6px 10px',
                      borderRadius: '20px',
                      fontSize: '13px',
                    }}
                  >
                    {tag}
                    <span
                      onClick={() => removeTag(tag)}
                      style={{
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      x
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p
              style={{
                margin: '0',
                fontSize: '13px',
                color: '#6b7280',
              }}
            >
              Separate tags with commas
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPostingForm;
